// Consent record for §25 TDDDG / Art. 6(1)(a) GDPR.
//
// Stored in a first-party cookie rather than localStorage for two reasons: the
// server can read it (so a future SSR pass can decide not to emit a tag at all),
// and a cookie has a real expiry, which is what makes the "ask again after 12
// months" rule enforceable rather than aspirational.
//
// The record is versioned. Bumping CONSENT_VERSION invalidates every stored
// choice and re-prompts everyone — which is the correct behaviour when a new
// processor is added, because consent given to the old set was never consent to
// the new one.

// Lives here rather than in CookieBanner so that the footer's "Cookie settings"
// control can dispatch it without importing the banner component (and dragging
// the banner + its CSS into the footer's chunk).
export const OPEN_SETTINGS_EVENT = 'ti:open-cookie-settings';

export const CONSENT_COOKIE = 'ti_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 365;

export const CATEGORIES = ['analytics', 'marketing'];

const listeners = new Set();
let cached;

function readCookie(name) {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name, value, maxAgeDays) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Max-Age=${Math.round(maxAgeDays * 86400)}` +
    `; Path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name) {
  if (typeof document === 'undefined') return;
  // Clear on the bare host and on the dot-domain, since GA writes to the latter.
  const host = window.location.hostname;
  const domains = [undefined, host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  for (const domain of domains) {
    document.cookie =
      `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}`;
  }
}

// A browser-level "do not sell/share" signal. The privacy policy commits to
// treating it as a withdrawal of consent, so it has to actually short-circuit
// the default state rather than merely be documented.
export function hasGlobalPrivacyControl() {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}

function emptyConsent(reason) {
  return { version: CONSENT_VERSION, analytics: false, marketing: false, decided: false, reason };
}

export function getConsent() {
  if (cached) return cached;

  const raw = readCookie(CONSENT_COOKIE);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.version === CONSENT_VERSION) {
        cached = {
          version: CONSENT_VERSION,
          analytics: parsed.analytics === true,
          marketing: parsed.marketing === true,
          decided: true,
          ts: parsed.ts,
        };
        return cached;
      }
    } catch (_e) {
      // Corrupted record — fall through and treat as undecided rather than
      // guessing, because guessing here means setting cookies without consent.
    }
  }

  cached = hasGlobalPrivacyControl()
    ? { ...emptyConsent('gpc'), decided: true }
    : emptyConsent('none');
  return cached;
}

export function setConsent({ analytics = false, marketing = false }) {
  const record = {
    version: CONSENT_VERSION,
    analytics: analytics === true,
    marketing: marketing === true,
    ts: new Date().toISOString(),
  };
  writeCookie(CONSENT_COOKIE, JSON.stringify(record), CONSENT_MAX_AGE_DAYS);
  cached = { ...record, decided: true };

  // Withdrawing consent has to remove what was already written, or the
  // withdrawal is cosmetic. GA4 writes _ga plus one _ga_<container> cookie;
  // OptinMonster writes om-* and _omappvp.
  if (!record.analytics) clearAnalyticsCookies();
  if (!record.marketing) clearMarketingCookies();

  listeners.forEach((fn) => {
    try {
      fn(cached);
    } catch (_e) {
      /* a broken subscriber must not block the others */
    }
  });
  return cached;
}

export function clearAnalyticsCookies() {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n === '_ga' || n.startsWith('_ga_') || n === '_gid' || n.startsWith('_gat'));
  names.forEach(deleteCookie);
}

export function clearMarketingCookies() {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n.startsWith('om-') || n.startsWith('omSeen-') || n.startsWith('_omapp'));
  names.forEach(deleteCookie);
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Test seam — resets module state between assertions.
export function __resetConsentCache() {
  cached = undefined;
}
